import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataQueryInformation } from '../models/data-query-information';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';
import { DataQueryObject } from '../models/data-query-object.model';
import { detailExpand } from '../../../shared-components/angular-material/mat-animations';
import { SharedService } from '../../../shared-components/shared.service';
import { MatDialog } from '@angular/material/dialog';
import { DataQueryField } from '../models/data-query-field.model';
import { DataQuery } from '../models/data-query.model';
import { DataQueryService } from '../data-query.service';
import { DataQueryPreferences } from '../../../shared-components/user-preferences/data-query-preferences.model';
import { LoginService } from '../../../shared-modules/login/login.service';
import { NestedData } from '../../../shared-components/virtualized-table/nested-data';
import * as jsPDF from 'jspdf';
import { DataQueryFieldFilter } from '../models/data-query-field-filter.model';
import { Subscription } from 'rxjs';
import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { VirtualizedTableComponent } from '../../../shared-components/virtualized-table/virtualized-table.component';
import { YearService } from '../../../shared-components/year/year.service';
import { Lookup } from '../../../shared-components/lookup/lookup.model';
import { GeneralConfirmationComponent } from '../../../shared-components/controls/general-confirmation/general-confirmation.component';
import { take } from 'rxjs/operators';
import { DataQueryCoreFile } from '../models/data-query-core-file.model';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { DropFieldWarningDialogComponent } from './drop-field-warning-dialog/drop-field-warning-dialog.component';
import { DataQueryCategory } from '../models/data-query-category.model';

@Component({
    selector: 'data-query-fields',
    templateUrl: './data-query-fields.component.html',
    styleUrls: [
        '../data-query-shared-style.css',
        './data-query-fields.component.css',
        '../virtualized-table.css'
    ],
    animations: [
        detailExpand
    ]
})
export class DataQueryFieldsComponent implements OnInit, AfterViewInit, OnDestroy {

    // Interface Properties
    @Input() simpleMode = false;
    @Input() simpleValidationErrors: ErrorList[] = [];

    // Properties
    dragging = false;
    lastDragPosition: MousePosition;
    diffX = 0;
    fieldSearch = '';
    toggleAllRows = new EventEmitter<boolean>();

    // State Properties
    private currentQuery: DataQuery;
    private selectedCoreFile: DataQueryCoreFile;
    V10: boolean;
    information = new DataQueryInformation();
    preferences = new DataQueryPreferences();
    subscriptions: Subscription[] = [];

    // Table Properties
    tableData: NestedData[] = [];
    shownFieldTableData = new TylerMatTableDataSource();
    filterFieldTableData = new TylerMatTableDataSource();
    virtualizedTableColumns = DataQueryInformation.virtualizedTableColumns;
    selectedRow: DataQueryObject;
    lastFieldClicked: DataQueryField;
    lastFieldClickTime: Date;

    // View Properties
    @ViewChild(VirtualizedTableComponent, { static: true }) virtualizedTable: VirtualizedTableComponent;
    virtualizedDropList: CdkDropList;

    // Class Functions
    constructor(
        private sharedService: SharedService,
        private dialog: MatDialog,
        private dataQueryService: DataQueryService,
        private loginService: LoginService,
        private yearService: YearService
    ) { }

    get shownFields(): DataQueryField[] {
        return this.currentQuery ? this.currentQuery.reportFields : [];
    }

    get filterFields(): DataQueryField[] {
        return this.currentQuery ? this.currentQuery.filterFields : [];
    }

    // Event Functions
    ngOnInit() {
        // POST: performs initialization and initial subscriptions
        this.V10 = this.loginService.LoginSettings.V10;

        // Performing subscriptions
        this.subscriptions.push(

            // Responding to the expand or collapse events
            this.toggleAllRows.subscribe(expand => {
                this.expandOrCollapseAll(expand);
            }),

            // Syncing data
            this.yearService.observableDefaultYearStream$.subscribe(() => {

                // Eliminating the cache
                this.dataQueryService.cachedInformation = false;

                // Obtaining data from the server
                this.dataQueryService.getInformation().subscribe();
            }),

            // Syncing this with the current query and information
            this.dataQueryService.observableCurrentQueryStream$.subscribe(currentQuery => {
                this.currentQuery = currentQuery;
                this.setTableData();
            }),
            this.dataQueryService.observableInformationStream$.subscribe(information => {
                this.information = information;
                this.setTableData();
            }),
            this.dataQueryService.observablePreferencesStream$.subscribe(preferences => {
                this.preferences = preferences;
                this.setTableData();
            }),
            this.dataQueryService.observableSelectedCoreFileStream$.subscribe(selectedCoreFile => {
                if (this.simpleMode) {
                    this.selectedCoreFile = selectedCoreFile;
                    this.setTableData();
                }
            }),
            this.dataQueryService.observableWizardOpenStream$.subscribe(() => {
                this.filterFieldTableData.data = this.filterFields;
            })
        );
    }

    ngAfterViewInit() {
        // POST: sets the virtualized drop list
        if (this.virtualizedTable) {
            this.virtualizedDropList = this.virtualizedTable.virtualizedDropList;
        }
    }

    ngOnDestroy() {
        // POST: unsubscribes to subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    dividerClick(event: MouseEvent) {
        // PRE: the divider is clicked
        // POST: sets the starting position of the drag
        this.lastDragPosition = new MousePosition(event);
        this.dragging = true;
    }

    dividerMove(event: MouseEvent) {
        // PRE: the mouse is moved over the draggable area and dragging is true
        // POST: moves the divider with the mouse
        if (this.dragging && this.lastDragPosition) {
            const currentDragPosition = new MousePosition(event);
            this.diffX += this.lastDragPosition.x - currentDragPosition.x;
            this.lastDragPosition = currentDragPosition;
        }
    }

    dividerStop() {
        // PRE: the mouse leaves the draggable area or releases
        // POST: stops dragging the divider
        this.dragging = false;
    }

    expandOrCollapseAll(expand: boolean) {
        // PRE: the expand all or collapse all button is clicked
        // POST: expand or collapses all the rows
        // NOTE: we must force change detection because the virtualized table uses OnPush
        const previousTableData = Object.assign([], this.tableData);
        this.tableData = [];
        const expandData = (data: NestedData[]) => {
            data.forEach(datum => {
                datum.showChildren = expand;
                if (datum.children) {
                    expandData(datum.children);
                }
            });
        };
        expandData(previousTableData);
        this.tableData = previousTableData;
    }

    fieldDrop(event: CdkDragDrop<DataQueryField[], DataQueryField[]>) {
        // PRE: a field is dropped into either the shown or filter fields
        // POST: moves the data from the available fields to the shown fields
        const field: DataQueryField = event.item.data;
        const droppingList = event.container.data;
        const dropFromList = event.previousContainer.data;

        // Returning if there is no field, because we can't do anything
        if (!field) {
            return;
        }

        // Returning if there is only filter field & container is "Show in Result".
        if (event.container.id == '#cdkDropListShowInResults' && (field.FieldName == 'Filter Any Period Attendance Code' || field.FieldName == 'Filter Any Period Room Number' || field.FieldName == 'Filter Any Period Subject Code' || field.FieldName == 'Filter Any Period Course' || field.FieldName == 'Filter Any Period Teacher' || field.FieldName == 'StudentsTeamfilter' || field.FieldName == 'StudentsListfilter')) {
            return;
        }

        // If there is no dropping list, then we remove it
        if (!droppingList && dropFromList) {

            // Helper function to perform the drop
            const performDrop = (droppedField = field) => {
                dropFromList.splice(dropFromList.findIndex(dropFromField => dropFromField.isEqual(droppedField)), 1);

                // If the drop from list is filter fields, remove the filters
                if (dropFromList == this.filterFields) {
                    droppedField.Filters = [];
                }

                // If the drop from list is shown fields, remove it from the sort fields as well
                const indexOfSort = this.currentQuery.sortFields.findIndex(sortField => sortField.isEqual(droppedField));
                if (dropFromList == this.shownFields && indexOfSort >= 0) {
                    this.currentQuery.sortFields.splice(indexOfSort, 1);
                }
            };

            // We always perform the drop on V10
            if (this.V10) {
                performDrop();
            } else {

                // Before we remove it, check the relationships
                const allFields = this.dataQueryService.getAllFields();
                const newFieldSet = allFields.filter(fieldOfSet =>
                    field.ID != fieldOfSet.ID
                    || field.CategoryID != fieldOfSet.CategoryID
                    || field.CategoryName != fieldOfSet.CategoryName
                );
                const clustersBefore = this.information.getClusters(allFields);
                const clustersAfter = this.information.getClusters(newFieldSet);

                // Showing the warning if we have more clusters, i.e. this was needed to join them
                if (clustersAfter.length > clustersBefore.length) {

                    // Finding the largest cluster
                    const largestCluster = clustersAfter.sort((a, b) => b.size - a.size)[0];

                    // Creating the warning
                    const removedFields = dropFromList.filter(dropFromField =>
                        !largestCluster.fields.find(clusterField =>
                            clusterField.isEqual(dropFromField)
                        )
                    );
                    const confirmDialog = this.dialog.open(DropFieldWarningDialogComponent, {
                        data: {
                            field: field,
                            removedFields: removedFields
                        }
                    });

                    // If the user confirms, perform the drop
                    confirmDialog.afterClosed().pipe(take(1)).subscribe(confirmed => {
                        if (confirmed) {

                            // Dropping all but the largest cluster
                            removedFields.forEach(removedField => {
                                performDrop(removedField);
                            });

                            // We must mark the query as changed
                            this.currentQuery.queryChanged = true;

                            // Resetting table data
                            setTimeout(() => {
                                this.setTableData();
                            });
                        }
                    });

                    // We need to return to prevent the end of this process from happening
                    return;
                }

                // Otherwise, just dropping
                else {
                    performDrop();
                }
            }
        }

        // If we're dropping into the same list, just reorder
        else if (dropFromList == droppingList) {

            // Rearrange their sort orders so it is reflected in results
            const previousIndex = droppingList.indexOf(field);
            droppingList[event.currentIndex].SortOrder = previousIndex;
            droppingList[previousIndex].SortOrder = event.currentIndex;

            // Moving the item in the array
            // NOTE: event.previousIndex is wrong, so we don't use it here
            moveItemInArray(droppingList, droppingList.indexOf(field), event.currentIndex);
        }

        // If we are dropping into a different list, add
        else {

            // If the dropping list is the shown fields and the field is a roster filer, we can't add it to the sort fields
            if (
                droppingList == this.shownFields &&
                field.DisplayName.includes(DataQueryFieldFilter.courseFilterDisplayName) ||
                field.FieldName.includes(DataQueryFieldFilter.studentFilterFieldName)
            ) {
                return;
            }

            // Otherwise, go ahead and add it
            this.addFieldToList(field, droppingList);
        }

        // We must mark the query as changed
        this.currentQuery.queryChanged = true;

        // Resetting table data
        setTimeout(() => {
            this.setTableData();
        });
    }

    applySearch(searchString: string) {
        // PRE: the available fields textbox is blurred
        // POST: applies the search criteria and expands all the fields
        setTimeout(() => {
            if (this.fieldSearch != searchString) {
                this.fieldSearch = searchString;
                this.setTableData();
                if (searchString) {
                    this.expandOrCollapseAll(true);
                } else {
                    this.expandOrCollapseAll(false);
                }
            }
        }, 0);
    }

    fieldList() {
        // PRE: the field list button is clicked
        // POST: opens a new window with a PDF for the fields available

        // Declaring the document
        const doc = new jsPDF('p', 'pt', 'letter');
        doc.setFont('helvetica');
        doc.setFontType('normal');

        // Setting constant values
        const ptPerInch = 72;
        const printMargins = .25 * ptPerInch;
        const margins = {
            top: 20 + printMargins,
            bottom: 10 + printMargins,
            left: printMargins,
            right: printMargins
        };
        const pageWidth = doc.internal.pageSize.width || (<any>doc.internal.pageSize).getWidth();
        const pageHeight = doc.internal.pageSize.height || (<any>doc.internal.pageSize).getHeight();
        const columns = 2;
        const colPadding = 10;
        const colWidth = ((pageWidth - margins.left - margins.right) / columns) - (colPadding * 2);
        const fontSize = 10;
        const headerFontSize = 12;
        const headerStartY = margins.top;
        const headerHeight = fontSize + 4;
        const tabIndent = 15;

        // Setting iterative values
        let x = margins.left;
        let y = Math.max(headerStartY + headerHeight + colPadding, margins.top);
        let col = 1;
        let pageCount = 1;
        let firstPage = true;

        // Creating helper functions
        const makeNewPage = () => {
            // POST: makes a new page
            col = 1;
            doc.setFontSize(fontSize);
            doc.setFontType('normal');
            doc.text(pageCount + '', pageWidth / 2, pageHeight - margins.bottom + 16, { align: 'center' });
            doc.addPage();
            pageCount += 1;
            firstPage = false;
        };
        const checkNewPage = () => {
            // POST: checks to see if we need to make a new page. If we do, does so.
            if (y > pageHeight - margins.bottom) {
                if (col < columns) {
                    col += 1;
                }
                else {
                    makeNewPage();
                }
                if (firstPage) {
                    y = Math.max(headerStartY + headerHeight + colPadding, margins.top);
                } else {
                    y = margins.top;
                }
            }
        };
        const printValues = (values: string[], indents = 0) => {
            // PRE: a list of string values to print and how indented you want it
            // POST: prints the values onto the document
            for (let i = 0; i < values.length; i++) {
                y += fontSize + 4;
                checkNewPage();
                x = margins.left + (tabIndent * indents) + colPadding + ((col - 1) * colWidth);
                doc.text(values[i], x, y);
            }
        };
        const splitAndPrint = (str: string, indents = 0, fontWeight = 'normal') => {
            // PRE: a string to print and how indented yo uwant it
            // POST: splits the string into properly sized components and prints them
            doc.setFontSize(fontSize);
            doc.setFontType(fontWeight);
            const splitStr = doc.splitTextToSize(str, colWidth - (tabIndent * indents));
            printValues(splitStr, indents);
        };

        // Creating the header
        doc.setFontSize(headerFontSize);
        doc.setFontType('normal');
        doc.text('Data Query Field List', pageWidth / 2, headerStartY, null, null, 'center');

        // Showing the spinner due to the time this takes
        this.sharedService.updateShowSpinner(true);

        // Giving the dom a second to update
        setTimeout(() => {

            // Adding each object
            this.information.visibleObjects.forEach(object => {
                splitAndPrint(object.ObjectName, 0, 'bold');

                // Adding object categories
                const categories = object.categories;
                categories.forEach(category => {
                    splitAndPrint(category.CategoryName, 1);

                    // Adding category fields
                    const fields = category.fields;
                    fields.forEach(field => {
                        if (field.Visible == true) {
                            splitAndPrint(field.DisplayName, 2);
                        }
                    });
                    y += colPadding;
                });
            });

            // Hiding the spinner now that we're done
            this.sharedService.updateShowSpinner(false);

            // Printing it to the user
            const blob = doc.output('blob');
            window.open(URL.createObjectURL(blob), '_blank');
        }, 100);
    }

    // Methods
    addFieldToList(field: DataQueryField, fieldList: DataQueryField[]) {
        // POST: Adds the field to the specified field list

        // If the list is filter fields, add a filter
        if (fieldList == this.filterFields) {
            this.dataQueryService.addFieldToQuery(field, 'filter');
        }

        // If the list is shown fields, remove all filters and add it to the sort fields
        if (fieldList == this.shownFields) {
            this.dataQueryService.addFieldToQuery(field, 'shown');
        }
    }

    setTableData() {
        // POST: sets the table data and detects changes to update the table
        if (this.information) {

            // If we don't show restricted categories, then we need to calculate the disabled fields ahead of time
            // NOTE: v9 only
            if (!this.V10 && this.preferences && !this.preferences.DisplayRestrictedCategories) {
                this.information.fields.forEach(field => {
                    this.dataQueryService.checkDisabledField(field);
                });
            }

            // List of available fields
            const fetchFields = (categories?: DataQueryCategory[]) => {
                this.tableData = this.information.virtualizedTableData({
                    fieldSearch: this.fieldSearch,
                    V10: this.loginService.LoginSettings.V10,
                    currentQuery: this.currentQuery,
                    currentNestedData: this.tableData,
                    click: (event: MouseEvent, nestedData: NestedData) => {
                        if (nestedData.identifier == 'field') {

                            // If this is a double click, add the field
                            if (
                                this.lastFieldClicked
                                && this.lastFieldClicked.ID == nestedData.data.ID
                                && this.lastFieldClicked.CategoryID == nestedData.data.CategoryID
                                && this.lastFieldClicked.CategoryName == nestedData.data.CategoryName
                                && this.lastFieldClickTime
                                && Date.now() - this.lastFieldClickTime.getTime() < 2000
                            ) {

                                // Adding the field
                                this.addFieldToList(this.lastFieldClicked, this.shownFields);

                                // Resetting our double click
                                this.lastFieldClicked = null;
                                this.lastFieldClickTime = null;

                                // Resetting the table data
                                this.setTableData();
                            }

                            // If this is a single click, keep that in mind
                            else {
                                this.lastFieldClicked = nestedData.data;
                                this.lastFieldClickTime = new Date();
                            }
                        }
                    },
                    preferences: this.preferences,
                    disabledFields: this.preferences && !this.preferences.DisplayRestrictedCategories ? this.dataQueryService.disabledFields : null,
                    validCategories: categories
                });
            };
            if (this.simpleMode) {
                if (this.selectedCoreFile) {
                    this.dataQueryService.getCategoriesByCoreFile(this.selectedCoreFile.ID).subscribe(categories => {
                        fetchFields(categories);
                    });
                }
            } else {
                fetchFields();
            }

            // Helper Function
            const largestCluster = this.information.getLargestClusterOfQuery(this.currentQuery);
            const hasRelationship = (field: DataQueryField) => {
                return !!largestCluster.fields.find(clusterField =>
                    clusterField.key == field.key
                    || !!this.information.getRelationship(clusterField, field)
                );
            };

            // Calculating the shown fields
            if (!this.V10) {
                this.shownFields.forEach(field => {
                    field.hasRelationship = hasRelationship(field);
                });
            }
            this.shownFieldTableData.data = this.shownFields;

            // Calculating the filter fields
            if (!this.V10) {
                this.filterFields.forEach(field => {
                    field.hasRelationship = hasRelationship(field);
                });
            }
            this.filterFieldTableData.data = this.filterFields;
        }
    }
}

// This class is for tracking mouse position
class MousePosition {

    // Properties
    x: number;
    y: number;

    // Constructor
    constructor(mousePos: MouseEvent) {
        this.x = mousePos.screenX;
        this.y = mousePos.screenY;
    }
}
